import { gql } from 'graphql-request';
import { useQuery } from '@tanstack/react-query';
import useGraphQLClient from '../../../../app/hooks/useGraphQLClient';
import { useAuthContext } from '../../../auth/contexts/AuthContext';
import IFund from '../../interfaces/fund/IFund';
import { useMemo } from 'react';
import { TInvestFundCurrency } from '../../types/fund/TInvestFundCurrency';
import useAppLog from '../../../../app/features/log/hooks/useAppLog';
import _ from 'lodash';

const useInvestFunds = (typeTag: string | null = null, currency: TInvestFundCurrency | null = null) => {
	const auth = useAuthContext();
	const graphQLClient = useGraphQLClient();
	const appLog = useAppLog();

	const { data, isLoading } = useQuery<IFund[] | null, Error>(
		['investmentFunds', typeTag, currency],
		async () => {
			try {
				const data = (await graphQLClient.request(
					gql`
						query {
							investmentFunds {
								id
								name
								middleOfficeId
								domainTag
								investmentYear
								vintage
								isRealized
								sfdr
								currency
								isCoinvestment
								reportingPeriods {
									tag
									name
									investorIds
									hasNav
								}
								typeTag
								investors {
									investmentYear
									company {
										id
										name
										group {
											id
											name
										}
									}
								}
								assets {
									name
									countryCode
									investment {
										reportingStatus
									}
								}
								assetClass
								classificationAMF
								accreditationDate
								risk
								placementDuration
								thematic
								labels
								isOpenEndedFund
								creationDate
							}
						}
					`,
				)) as { investmentFunds: IFund[] };

				const investmentFundsDataTmp = _.cloneDeep(
					data.investmentFunds.filter((x) => currency === null || x.currency === currency),
				);

				return investmentFundsDataTmp.filter((x) => !typeTag || x.typeTag === typeTag);
			} catch (e) {
				appLog.exception(e as Error, { component: 'useInvestFunds' });
				// Return null (not []) so consumers like LInvestor can distinguish
				// "no funds for this user" from a fetch error.
				return null;
			}
		},
		{
			// The query will not execute until the userId exists
			enabled: appLog && auth.currentCompany !== null && auth.isInvestor === true && auth.isAuthenticated,
		},
	);

	const fundsByKey = useMemo(
		() => data?.reduce((res, x) => ({ ...res, [x.id]: x }), {} as { [x: number]: IFund }),
		[data],
	);

	return {
		isLoading,
		funds: data,
		fundsByKey,
	};
};

export default useInvestFunds;
